import { Injectable } from '@angular/core';
import {
  RegistrationAccountDraft,
  RegistrationDraftState,
  RegistrationVerificationSession,
  RegistrationStep,
  SignupProfileDraft
} from '../models/aviator-registration-page.model';

const registrationDraftStorageKey = 'aoRegistrationDraft';
const registrationDraftLifetimeMs = 2 * 24 * 60 * 60 * 1000;
const verificationSessionLifetimeMs = 10 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class AviatorRegistrationDraftService {
  loadDraft(): RegistrationDraftState | null {
    const draft = this.readDraft();

    if (!draft) {
      return null;
    }

    if (this.isExpired(draft)) {
      this.clearDraft();
      return null;
    }

    return draft;
  }

  saveDraft(
    account: RegistrationAccountDraft,
    profile: SignupProfileDraft,
    registrationStep: RegistrationStep,
    showCountryLookup: boolean,
    verificationSession?: RegistrationVerificationSession | null
  ): void {
    const expiresAtUtc = new Date(Date.now() + registrationDraftLifetimeMs).toISOString();
    const draft: RegistrationDraftState = {
      account: {
        userName: account?.userName || '',
        email: account?.email || '',
        firstName: account?.firstName || '',
        lastName: account?.lastName || '',
        lockEmail: !!account?.lockEmail
      },
      profile: {
        ...profile
      },
      registrationStep,
      showCountryLookup,
      verificationSession: this.normalizeVerificationSession(verificationSession),
      expiresAtUtc
    };

    try {
      localStorage.setItem(registrationDraftStorageKey, JSON.stringify(draft));
    } catch {
      // Storage unavailable or full - draft persistence just won't stick.
    }
  }

  clearDraft(): void {
    try {
      localStorage.removeItem(registrationDraftStorageKey);
    } catch {
      // Storage unavailable - nothing to clear.
    }
  }

  updateDraftAccountEmail(email: string, userName?: string): void {
    const normalizedEmail = (email || '').trim();
    const normalizedUserName = (userName || '').trim() || normalizedEmail;

    if (!normalizedEmail) {
      return;
    }

    const draft = this.loadDraft();

    if (!draft?.account) {
      return;
    }

    this.saveDraft(
      {
        ...draft.account,
        email: normalizedEmail,
        userName: normalizedUserName
      },
      draft.profile,
      draft.registrationStep,
      draft.showCountryLookup,
      draft.verificationSession
    );
  }

  private readDraft(): RegistrationDraftState | null {
    try {
      const draftJson = localStorage.getItem(registrationDraftStorageKey);

      if (!draftJson) {
        return null;
      }

      return JSON.parse(draftJson) as RegistrationDraftState;
    } catch {
      return null;
    }
  }

  private isExpired(draft: RegistrationDraftState): boolean {
    const expiresAtMs = Date.parse(draft?.expiresAtUtc || '');

    if (!Number.isFinite(expiresAtMs)) {
      return true;
    }

    return expiresAtMs <= Date.now();
  }

  private normalizeVerificationSession(verificationSession?: RegistrationVerificationSession | null): RegistrationVerificationSession | null {
    if (!verificationSession?.registrationSessionToken || !verificationSession?.email) {
      return null;
    }

    const expiresAtMs = Date.parse(verificationSession.expiresAtUtc || '');
    const expiresAtUtc = Number.isFinite(expiresAtMs)
      ? verificationSession.expiresAtUtc
      : new Date(Date.now() + verificationSessionLifetimeMs).toISOString();

    return {
      email: verificationSession.email,
      registrationSessionToken: verificationSession.registrationSessionToken,
      registrationVerificationProof: verificationSession.registrationVerificationProof,
      expiresAtUtc
    };
  }
}
